import { apiClient } from './apiClient'

// ============================================
// TIPOS
// ============================================

export interface Post {
  id: string
  title: string
  slug: string
  summary: string | null
  content: string | null
  status: 'draft' | 'published' | 'archived'
  author_id: string
  cover_image_url: string | null
  metadata: Record<string, any>
  view_count: number
  created_at: string
  updated_at: string
  published_at: string | null
  deleted_at: string | null
  taxonomies?: Array<{
    id: string
    name: string
    slug: string
    type: string
  }>
}

export interface CreatePostInput {
  title: string
  slug?: string
  summary?: string
  content?: string
  status?: 'draft' | 'published' | 'archived'
  cover_image_url?: string
  metadata?: Record<string, any>
  taxonomy_ids?: string[]
}

export interface UpdatePostInput {
  title?: string
  slug?: string
  summary?: string
  content?: string
  status?: 'draft' | 'published' | 'archived'
  cover_image_url?: string
  metadata?: Record<string, any>
  taxonomy_ids?: string[]
}

export interface ListPostsParams {
  page?: number
  limit?: number
  status?: string
  author_id?: string
  taxonomy_id?: string
  search?: string
  includeDeleted?: boolean
}

export interface ListPostsResponse {
  data: Post[]
  pagination: {
    page: number
    limit: number
    total: number
    total_pages: number
  }
}

// ============================================
// SERVIÇO
// ============================================

class PostsService {
  /**
   * Listar posts com paginação e filtros
   */
  async listPosts(params?: ListPostsParams): Promise<ListPostsResponse> {
    const response = await apiClient.get<ListPostsResponse>('/content/posts', { params })
    return response.data
  }

  /**
   * Buscar post por ID
   */
  async getPostById(id: string): Promise<Post> {
    const response = await apiClient.get<Post>(`/content/posts/id/${id}`)
    return response.data
  }

  /**
   * Buscar post por slug
   */
  async getPostBySlug(slug: string): Promise<Post> {
    const response = await apiClient.get<Post>(`/content/posts/${slug}`)
    return response.data
  }

  /**
   * Criar post
   */
  async createPost(data: CreatePostInput): Promise<Post> {
    const response = await apiClient.post<Post>('/content/posts', data)
    return response.data
  }

  /**
   * Atualizar post
   */
  async updatePost(id: string, data: UpdatePostInput): Promise<Post> {
    const response = await apiClient.put<Post>(`/content/posts/${id}`, data)
    return response.data
  }

  /**
   * Deletar post (soft delete)
   */
  async deletePost(id: string): Promise<void> {
    await apiClient.delete(`/content/posts/${id}`)
  }

  /**
   * Restaurar post deletado
   */
  async restorePost(id: string): Promise<void> {
    await apiClient.post(`/content/posts/${id}/restore`)
  }
}

export default new PostsService()
